import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AuthCanvas from '@/components/AuthCanvas';
import '@/styles/auth.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
const reEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Une erreur est survenue';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('. ');
  return String(d);
}
function scorePassword(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) s++;
  return s;
}
const PWD_LABELS = ['', 'Faible', 'Moyen', 'Bon', 'Excellent'];
const PWD_CLASS  = ['', 'w', 'm', 'm', 's'];

function Toast({ message, type, show }) {
  return (
    <div
      className={`c2s-toast ${show ? 'on' : ''} ${type === 'success' ? 'toast--success' : ''}`}
      role="status" aria-live="polite"
    >
      {message}
    </div>
  );
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Un membre connecté n'a rien à faire ici (comme la connexion et l'inscription) : son espace
  useEffect(() => {
    if (user) navigate(user.roles?.includes('ROLE_ADMIN') ? '/admin/dashboard' : '/app/dashboard', { replace: true });
  }, [user, navigate]);
  const [state, setState] = useState(1); // 1 email | 2 otp | 3 new pwd | 4 success

  const [email, setEmail]     = useState('');
  const [emailErr, setEmailErr] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);

  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpErr, setOtpErr] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetToken, setResetToken] = useState('');
  const otpRefs = useRef([]);

  const [newPwd, setNewPwd]   = useState('');
  const [confPwd, setConfPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [confErr, setConfErr] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [toast, setToast] = useState({ message: '', type: 'default', show: false });
  const toastTimer = useRef(null);
  const showToast = (message, type = 'default') => {
    setToast({ message, type, show: true });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 2800);
  };

  /* Resend cooldown */
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  /* Auto-focus first OTP input on state 2 */
  useEffect(() => {
    if (state === 2 && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 350);
    }
  }, [state]);

  /* ──────── ÉTAT 1 — Envoi OTP ──────── */
  const doSendCode = async () => {
    if (!reEmail.test(email.trim())) {
      setEmailErr(true);
      showToast('Adresse email invalide');
      return;
    }
    setEmailErr(false);
    setSendLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setOtpDigits(['', '', '', '', '', '']);
      setOtpErr(false);
      setResendCooldown(60);
      setState(2);
      showToast('Code envoyé !', 'success');
    } catch (err) {
      showToast(formatError(err));
    } finally {
      setSendLoading(false);
    }
  };

  /* ──────── ÉTAT 2 — OTP input ──────── */
  const onOtpChange = (idx, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    setOtpDigits(prev => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    setOtpErr(false);
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
  };
  const onOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };
  const onOtpPaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < txt.length; i++) next[i] = txt[i];
    setOtpDigits(next);
    otpRefs.current[Math.min(txt.length, 5)]?.focus();
  };

  const doVerify = async () => {
    const code = otpDigits.join('');
    if (code.length !== 6) {
      setOtpErr(true);
      showToast('Veuillez saisir les 6 chiffres');
      return;
    }
    setVerifyLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: code,
      });
      setResetToken(data.token);
      setState(3);
      showToast('Code validé ✓', 'success');
    } catch (err) {
      setOtpErr(true);
      showToast(formatError(err));
    } finally {
      setVerifyLoading(false);
    }
  };

  const doResend = async () => {
    if (resendCooldown > 0) return;
    setSendLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setResendCooldown(60);
      setOtpDigits(['', '', '', '', '', '']);
      setOtpErr(false);
      otpRefs.current[0]?.focus();
      showToast('Nouveau code envoyé !', 'success');
    } catch (err) {
      showToast(formatError(err));
    } finally {
      setSendLoading(false);
    }
  };

  /* ──────── ÉTAT 3 — Nouveau mot de passe ──────── */
  const doReset = async () => {
    const score = scorePassword(newPwd);
    if (newPwd.length < 8 || score < 2 || !/[A-Z]/.test(newPwd) || !/[0-9]/.test(newPwd)) {
      showToast('Mot de passe trop faible (8 car. min., 1 maj., 1 chiffre)');
      return;
    }
    if (newPwd !== confPwd) {
      setConfErr(true);
      showToast('Les mots de passe ne correspondent pas');
      return;
    }
    setConfErr(false);
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        password: newPwd,
      });
      setState(4);
      showToast('Mot de passe réinitialisé ✓', 'success');
    } catch (err) {
      showToast(formatError(err));
    } finally {
      setResetLoading(false);
    }
  };

  /* ──────── Indicators ──────── */
  const progressWidth = state === 1 ? 33 : state === 2 ? 66 : 100;
  const pwdScore = scorePassword(newPwd);

  return (
    <div className="auth-card-page" data-testid="forgot-password-page">
      <AuthCanvas />
      <div className="visual-overlay" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />

      <div className="card">
        <div className="card-bar" />
        <div className="card-body">
          <div className="brand">
            <div className="brand-mark">C2</div>
            <span className="brand-name">Colis<em>2</em>Ship</span>
          </div>

          {/* Progress */}
          <div className="progress">
            <div className="progress-fill" style={{ width: `${progressWidth}%` }} />
          </div>

          {/* Dots */}
          <div className="step-dots">
            <div className={`sdot ${state === 1 ? 'on' : state > 1 ? 'done' : ''}`} />
            <div className={`sdot ${state === 2 ? 'on' : state > 2 ? 'done' : ''}`} />
            <div className={`sdot ${state === 3 ? 'on' : state > 3 ? 'done' : ''}`} />
          </div>

          {/* ÉTAT 1 — Email */}
          <div className={`state ${state === 1 ? 'active' : ''}`}>
            <div className="state-ico state-ico--lock">🔐</div>
            <h2 className="state-title">Mot de passe oublié ?</h2>
            <p className="state-sub">
              Saisissez votre adresse email. Nous vous enverrons un code de vérification à 6 chiffres.
            </p>

            <div className="fg">
              <label className="flbl" htmlFor="fpEmail">Adresse email</label>
              <div className="fi-wrap">
                <input
                  type="email" id="fpEmail" className={`fi ${emailErr ? 'err' : (email && reEmail.test(email) ? 'ok' : '')}`}
                  placeholder="votre@email.com" autoComplete="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setEmailErr(false); }}
                  onKeyDown={e => e.key === 'Enter' && doSendCode()}
                  data-testid="input-email"
                />
                <span className="fi-ico">✉</span>
              </div>
              {emailErr && <div className="ferr on">Adresse email invalide</div>}
            </div>

            <button
              type="button" className="cta cta--primary"
              onClick={doSendCode} disabled={sendLoading}
              data-testid="btn-send-code"
            >
              {sendLoading && <span className="cta-ldr" />}
              <span>{sendLoading ? 'Envoi…' : 'Envoyer le code'}</span>
            </button>
          </div>

          {/* ÉTAT 2 — OTP */}
          <div className={`state ${state === 2 ? 'active' : ''}`}>
            <div className="state-ico state-ico--mail">📬</div>
            <h2 className="state-title">Vérifiez votre boîte mail</h2>
            <p className="state-sub">
              Nous avons envoyé un code à 6 chiffres à <strong data-testid="email-display">{email}</strong>.
              Il expire dans 10 minutes.
            </p>

            <div className="fg">
              <label className="flbl" style={{ textAlign: 'center', display: 'block', marginBottom: 12 }}>
                Code de vérification
              </label>
              <div className="otp-row">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    className={`otp-input ${otpErr ? 'err' : ''}`}
                    maxLength={1} type="text" inputMode="numeric"
                    pattern="[0-9]" autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    value={d}
                    onChange={e => onOtpChange(i, e.target.value)}
                    onKeyDown={e => onOtpKeyDown(i, e)}
                    onPaste={i === 0 ? onOtpPaste : undefined}
                    data-testid={`input-otp-${i}`}
                  />
                ))}
              </div>
              {otpErr && <div className="ferr on">Code incorrect. Veuillez réessayer</div>}
            </div>

            <button
              type="button" className="cta cta--primary"
              onClick={doVerify} disabled={verifyLoading}
              data-testid="btn-verify-otp"
            >
              {verifyLoading && <span className="cta-ldr" />}
              <span>{verifyLoading ? 'Vérification…' : 'Vérifier le code'}</span>
            </button>

            <div className="resend-row">
              Pas reçu ?{' '}
              <button
                type="button" className="resend-btn"
                onClick={doResend} disabled={resendCooldown > 0 || sendLoading}
                data-testid="btn-resend-otp"
              >
                Renvoyer
              </button>
              {resendCooldown > 0 && (
                <span className="resend-timer"> · dans {resendCooldown}s</span>
              )}
            </div>
          </div>

          {/* ÉTAT 3 — Nouveau mot de passe */}
          <div className={`state ${state === 3 ? 'active' : ''}`}>
            <div className="state-ico state-ico--lock">🔑</div>
            <h2 className="state-title">Nouveau mot de passe</h2>
            <p className="state-sub">Choisissez un mot de passe fort pour sécuriser votre compte Colis2Ship.</p>

            <div className="fg">
              <label className="flbl" htmlFor="newPwd">Nouveau mot de passe</label>
              <div className="fi-wrap">
                <input
                  type={showPwd ? 'text' : 'password'} id="newPwd" className="fi"
                  placeholder="Minimum 8 caractères" autoComplete="new-password"
                  value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  data-testid="input-new-password"
                />
                <button
                  type="button" className="fi-ico fi-ico--eye"
                  onClick={() => setShowPwd(s => !s)}
                  aria-label={showPwd ? 'Masquer' : 'Afficher'}
                >
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
              <div className="pwd-bars">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`pwd-b ${i <= pwdScore ? PWD_CLASS[pwdScore] : ''}`} />
                ))}
              </div>
              <div className={`pwd-lbl ${PWD_CLASS[pwdScore]}`}>{PWD_LABELS[pwdScore]}</div>
            </div>

            <div className="fg">
              <label className="flbl" htmlFor="confPwd">Confirmer le mot de passe</label>
              <div className="fi-wrap">
                <input
                  type={showConf ? 'text' : 'password'} id="confPwd"
                  className={`fi ${confErr ? 'err' : (confPwd && newPwd === confPwd ? 'ok' : '')}`}
                  placeholder="••••••••" autoComplete="new-password"
                  value={confPwd}
                  onChange={e => { setConfPwd(e.target.value); setConfErr(false); }}
                  data-testid="input-confirm-password"
                />
                <button
                  type="button" className="fi-ico fi-ico--eye"
                  onClick={() => setShowConf(s => !s)}
                  aria-label={showConf ? 'Masquer' : 'Afficher'}
                >
                  {showConf ? '🙈' : '👁'}
                </button>
              </div>
              {confErr && <div className="ferr on">Les mots de passe ne correspondent pas</div>}
            </div>

            <div className="fhint" style={{ marginBottom: 16 }}>
              ✓ 8 caractères minimum &nbsp;·&nbsp; ✓ Lettre majuscule &nbsp;·&nbsp; ✓ Chiffre
            </div>

            <button
              type="button" className="cta cta--primary"
              onClick={doReset} disabled={resetLoading}
              data-testid="btn-reset-password"
            >
              {resetLoading && <span className="cta-ldr" />}
              <span>{resetLoading ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}</span>
            </button>
          </div>

          {/* ÉTAT 4 — Succès */}
          <div className={`state ${state === 4 ? 'active' : ''}`}>
            <div className="state-ico state-ico--success" style={{ fontSize: 26 }}>✓</div>
            <h2 className="state-title">Mot de passe réinitialisé !</h2>
            <p className="state-sub">
              Votre mot de passe a été mis à jour avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>

            <button
              type="button" className="cta cta--success"
              onClick={() => navigate('/login')}
              data-testid="btn-go-login"
            >
              Se connecter maintenant →
            </button>

            <div style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: 'var(--c2s-ink3)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', background: 'var(--c2s-tlt)',
                  display: 'inline-block', animation: 'c2s-pls 1.8s infinite'
                }} />
                Votre session est sécurisée
              </span>
            </div>
          </div>
        </div>

        <div className="card-footer">
          <button type="button" onClick={() => navigate('/login')} data-testid="back-to-login">
            ← Retour à la connexion
          </button>
          {' · '}
          <button type="button" onClick={() => showToast('Support : support@colis2ship.com')}>
            Aide
          </button>
        </div>
      </div>

      <Toast message={toast.message} type={toast.type} show={toast.show} />
    </div>
  );
}
